import { getCurrentOs } from "../util/os.js";
import { createContext, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";

//#region src/components/PwaProvider.tsx
const PwaContext = createContext({
	pwaId: "",
	appName: "",
	pwaAssets: {},
	canInstall: false,
	isInstalled: false,
	isStandalone: false,
	installApp: () => {},
	addContact: () => {}
});
const InnerPwaContext = createContext({
	setShowAddToContacts: () => {},
	setShowIosInstallOverlay: () => {},
	showAddToContacts: false,
	showIosInstallOverlay: false
});
const isStandalone = navigator?.standalone ?? window.matchMedia("(display-mode: standalone)").matches;
const userOs = getCurrentOs();
const PwaProvider = ({ children, pwaId, appName, pwaAssets }) => {
	const [canInstall, setCanInstall] = useState(userOs === "ios");
	const [isInstalled, setIsInstalled] = useState(isStandalone);
	const [showIosInstallOverlay, setShowIosInstallOverlay] = useState(false);
	const installPrompt = useRef(null);
	const [showAddToContacts, setShowAddToContacts] = useState(false);
	const addContact = useCallback(() => {
		setShowAddToContacts(true);
	}, [setShowAddToContacts]);
	const installApp = useCallback(() => {
		if (userOs === "ios") {
			setShowIosInstallOverlay(true);
			return;
		}
		if (!installPrompt.current) return;
		installPrompt.current.prompt();
	}, []);
	const beforeInstallListener = useCallback((event) => {
		event.preventDefault();
		installPrompt.current = event.detail;
		setCanInstall(true);
	}, []);
	const appInstalledListener = useCallback(() => setIsInstalled(true), []);
	useLayoutEffect(() => {
		window.addEventListener("appinstalled", appInstalledListener);
		window.addEventListener("appinstallprompt", beforeInstallListener);
		return () => {
			window.removeEventListener("appinstalled", appInstalledListener);
			window.removeEventListener("appinstallprompt", beforeInstallListener);
		};
	}, [beforeInstallListener, appInstalledListener]);
	const values = useMemo(() => ({
		pwaId,
		appName,
		pwaAssets,
		canInstall: canInstall && !isStandalone,
		isInstalled,
		isStandalone,
		installApp,
		addContact
	}), [
		addContact,
		appName,
		canInstall,
		installApp,
		isInstalled,
		pwaAssets,
		pwaId
	]);
	const innerCtx = useMemo(() => ({
		setShowAddToContacts,
		setShowIosInstallOverlay,
		showAddToContacts,
		showIosInstallOverlay
	}), [
		setShowAddToContacts,
		setShowIosInstallOverlay,
		showAddToContacts,
		showIosInstallOverlay
	]);
	return /* @__PURE__ */ jsx(PwaContext.Provider, {
		value: values,
		children: /* @__PURE__ */ jsx(InnerPwaContext.Provider, {
			value: innerCtx,
			children
		})
	});
};

//#endregion
export { InnerPwaContext, PwaContext, PwaProvider };